import React, { useEffect, useRef, useState, useCallback } from 'react'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

// ─── Symbols: id → filename (transparent PNG, 200×200) ───────────────────────
const SYMS = ['star','blue_gem','purple_gem','orange_gem','yellow_gem','green_gem','bar','seven']
const SPATH = (id:number) => `/starburst/symbols/${SYMS[id]}.png`

// ─── Symbol glow colours ──────────────────────────────────────────────────────
const SYM_GLOW: Record<number,string> = {
  0:'#ff9900', 1:'#2979ff', 2:'#ab47bc',
  3:'#ff6d00', 4:'#ffd600', 5:'#00e676', 6:'#d4a017', 7:'#ff3333',
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const ROWS      = 3
const NUM_REELS = 5
const GAP       = 5
const PRE_SPIN  = 20

// ─── Measure hook ─────────────────────────────────────────────────────────────
function useWidth(ref:React.RefObject<HTMLElement>){
  const [w,setW]=useState(340)
  useEffect(()=>{
    if(!ref.current) return
    const ro=new ResizeObserver(e=>setW(e[0].contentRect.width))
    ro.observe(ref.current); setW(ref.current.clientWidth||340)
    return ()=>ro.disconnect()
  },[ref])
  return w
}

// ─── Build reel strip ─────────────────────────────────────────────────────────
function buildStrip(final:[number,number,number]):number[]{
  const pre=Array.from({length:PRE_SPIN},()=>Math.floor(Math.random()*8))
  return [...pre,...final,Math.floor(Math.random()*8),Math.floor(Math.random()*8)]
}

const easeOutQuart=(t:number)=>1-(--t)*t*t*t

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props { token:string; balance:number|null; playerId?:string; operatorId?:string }
type Phase='idle'|'spinning'|'expanding'|'respinning'|'win'

interface Win {
  line:number; symbol:number; count:number; payout:number
  positions:Array<[number,number]>; direction:'ltr'|'rtl'
}

// ─── Single Reel ─────────────────────────────────────────────────────────────
interface ReelProps {
  strip:number[]; spinning:boolean; targetY:number
  winRows:Set<number>; isWild:boolean
  cellW:number; cellH:number; reelIdx:number
}
function Reel({strip,spinning,targetY,winRows,isWild,cellW,cellH,reelIdx}:ReelProps){
  const drumRef=useRef<HTMLDivElement>(null)
  const rafRef=useRef(0)
  const curY=useRef(0)

  useEffect(()=>{
    cancelAnimationFrame(rafRef.current)
    if(!drumRef.current) return
    if(spinning){
      drumRef.current.style.transition='none'
      drumRef.current.style.transform='translateY(0)'
      curY.current=0
    } else {
      const from=curY.current, to=targetY
      const dur=800+reelIdx*160
      const t0=performance.now()
      const tick=(now:number)=>{
        const p=Math.min((now-t0)/dur,1)
        const y=from+(to-from)*easeOutQuart(p)
        if(drumRef.current) drumRef.current.style.transform=`translateY(${y}px)`
        curY.current=y
        if(p<1) rafRef.current=requestAnimationFrame(tick)
      }
      rafRef.current=requestAnimationFrame(tick)
    }
    return()=>cancelAnimationFrame(rafRef.current)
  },[spinning,targetY,reelIdx])

  const glowColor = isWild ? '#ff9900' : 'transparent'

  return(
    <div style={{
      width:cellW, flexShrink:0,
      height:ROWS*cellH+(ROWS-1)*3,
      overflow:'hidden', borderRadius:6, position:'relative',
      background: isWild
        ? 'linear-gradient(180deg,rgba(255,120,0,.25),rgba(255,60,0,.15))'
        : 'rgba(0,0,0,.5)',
      border:`1.5px solid ${isWild?'rgba(255,150,0,.8)':'rgba(255,255,255,.08)'}`,
      boxShadow: isWild ? `0 0 20px rgba(255,120,0,.5), inset 0 0 20px rgba(255,100,0,.2)` : 'none',
      transition:'all .4s ease',
    }}>
      <div ref={drumRef} style={{position:'absolute',top:0,left:0,width:'100%'}}>
        {strip.map((sym,i)=>{
          const isResult=i>=PRE_SPIN&&i<PRE_SPIN+ROWS
          const rowIdx=i-PRE_SPIN
          const isWin=isResult&&winRows.has(rowIdx)
          const g=SYM_GLOW[sym]||'#fff'
          return(
            <div key={i} style={{
              width:cellW, height:cellH,
              display:'flex',alignItems:'center',justifyContent:'center',
              marginBottom:3,
              background:isWin?`${g}22`:'transparent',
              outline:isWin?`2px solid ${g}`:isWild&&isResult?'1px solid rgba(255,150,0,.4)':'none',
              borderRadius:5,
              transition:'all .3s',
            }}>
              <img src={SPATH(sym)} alt={SYMS[sym]}
                style={{
                  width:Math.round(cellW*.80),height:Math.round(cellH*.80),
                  objectFit:'contain',
                  filter: isWin||isWild&&isResult
                    ? `drop-shadow(0 0 8px ${g}) drop-shadow(0 0 4px ${g})`
                    : 'none',
                  transition:'filter .3s',
                }}
              />
            </div>
          )
        })}
      </div>
      {/* Edge shadows */}
      <div style={{position:'absolute',top:0,left:0,right:0,height:22,
        background:'linear-gradient(to bottom,rgba(0,0,0,.85),transparent)',
        pointerEvents:'none',zIndex:2}}/>
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:22,
        background:'linear-gradient(to top,rgba(0,0,0,.85),transparent)',
        pointerEvents:'none',zIndex:2}}/>
    </div>
  )
}

// ─── Big Win overlay ──────────────────────────────────────────────────────────
function BigWin({amount,onDone}:{amount:number;onDone:()=>void}){
  const [displayed,setDisplayed]=useState(0)
  useEffect(()=>{
    let start=0; const target=amount; const dur=2000; const t0=performance.now()
    const tick=(now:number)=>{
      const p=Math.min((now-t0)/dur,1)
      setDisplayed(Math.floor(target*easeOutQuart(p)*100)/100)
      if(p<1) requestAnimationFrame(tick)
      else{ setDisplayed(target); setTimeout(onDone,1400) }
    }
    requestAnimationFrame(tick)
  },[amount,onDone])

  const label=amount>=500?'🌟 MEGA WIN!':amount>=100?'💥 BIG WIN!':'✨ WIN!'
  return(
    <div style={{
      position:'absolute',inset:0,zIndex:50,
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
      background:'rgba(0,0,0,.82)',backdropFilter:'blur(3px)',
    }}>
      <style>{`
        @keyframes sbPop{0%{transform:scale(.3);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}
        @keyframes sbRays{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
      `}</style>
      {/* Rays */}
      <div style={{position:'absolute',width:280,height:280,
        background:'conic-gradient(rgba(255,200,0,.12) 0deg,transparent 30deg,rgba(255,200,0,.12) 60deg,transparent 90deg,rgba(255,200,0,.12) 120deg,transparent 150deg,rgba(255,200,0,.12) 180deg,transparent 210deg,rgba(255,200,0,.12) 240deg,transparent 270deg,rgba(255,200,0,.12) 300deg,transparent 330deg)',
        borderRadius:'50%',animation:'sbRays 8s linear infinite',
      }}/>
      <div style={{animation:'sbPop .5s ease-out forwards',textAlign:'center',zIndex:1}}>
        <div style={{fontSize:28,fontWeight:900,color:'#ffd700',
          textShadow:'0 0 20px #ff9900,0 0 40px #ff6600',marginBottom:8}}>
          {label}
        </div>
        <div style={{fontSize:42,fontWeight:900,color:'#fff',
          textShadow:'0 0 30px #ffd700',fontVariantNumeric:'tabular-nums'}}>
          ₹{displayed.toFixed(2)}
        </div>
      </div>
    </div>
  )
}

// ─── Main game ────────────────────────────────────────────────────────────────
export function StarburstSlot({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const wrapRef  =useRef<HTMLDivElement>(null)
  const socketRef=useRef<Socket|null>(null)
  const sessionRef=useRef({playerId:initPid||'',operatorId:initOid||''})
  const timers   =useRef<any[]>([])
  const safetyRef =useRef<any>(null)

  const containerW=useWidth(wrapRef)
  const CELL_W=Math.min(100,Math.max(52,Math.floor((containerW-16-(NUM_REELS-1)*GAP)/NUM_REELS)))
  const CELL_H=Math.round(CELL_W*0.95)
  const targetY=-(PRE_SPIN*(CELL_H+3))

  const [grid,setGrid]     =useState<number[][]>([[4,5,6],[2,3,1],[5,6,4],[1,2,5],[6,4,3]])
  const [strips,setStrips] =useState<number[][]>(
    Array(NUM_REELS).fill(null).map((_,r)=>buildStrip([grid[r][0],grid[r][1],grid[r][2]] as any))
  )
  const [spinning,setSpinning]=useState<boolean[]>(Array(NUM_REELS).fill(false))
  const [wildReels,setWildReels]=useState<number[]>([])
  const [wins,setWins]     =useState<Win[]>([])
  const [phase,setPhase]   =useState<Phase>('idle')
  const [balance,setBalance]=useState<number|null>(initBalance)
  const [totalWin,setTotalWin]=useState(0)
  const [roundWin,setRoundWin]=useState(0)
  const [stake,setStake]   =useState(20)
  const [toast,setToast]   =useState('')
  const [showBigWin,setShowBigWin]=useState(false)
  const [currency,setCurrency]=useState('coins')
  const [reSpinsLeft,setReSpinsLeft]=useState(0)

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,dur=2200)=>{setToast(msg);setTimeout(()=>setToast(''),dur)}

  // Win cells for a reel
  const reelWinRows=(r:number)=>{
    const s=new Set<number>()
    wins.forEach(w=>w.positions.forEach(([ri,row])=>{ if(ri===r) s.add(row) }))
    return s
  }

  // Spin animation
  const triggerSpin=useCallback((resultGrid:number[][])=>{
    const ns=resultGrid.map(col=>buildStrip([col[0],col[1],col[2]] as any))
    setStrips(ns); setSpinning(Array(NUM_REELS).fill(true))
    setWins([]); setWildReels([])
    timers.current.forEach(clearTimeout); timers.current=[]
    ;[1300,1600,1900,2200,2500].forEach((d,i)=>{
      const t=setTimeout(()=>{
        setSpinning(p=>{ const n=[...p]; n[i]=false; return n })
        Sounds.land()
      },d)
      timers.current.push(t)
    })
    const t=setTimeout(()=>setSpinning(Array(NUM_REELS).fill(false)),3000)
    timers.current.push(t)
  },[])

  // Socket
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json()
        if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    // Initial spin result
    s.on('sb:result',(d:any)=>{
      const g:number[][]=d.grid
      setGrid(g); triggerSpin(g)
      setTimeout(()=>{
        setWins(d.wins||[]); setWildReels(d.expanding_wilds||[])
        const tw=d.total_win||0
        if(tw>0){ setTotalWin(tw); Sounds.hop() }
        if(d.expanding_wilds?.length>0){
          setPhase('expanding')
          setReSpinsLeft(d.re_spins_left||2)
          showToast('🌟 WILD EXPANDING!',2000)
          Sounds.go()
        } else {
          setPhase('idle')
        }
      },3200)
    })

    // Re-spin result
    s.on('sb:respin',(d:any)=>{
      const g:number[][]=d.grid
      setGrid(g); triggerSpin(g)
      setTimeout(()=>{
        setWins(d.wins||[])
        setWildReels(Object.keys(d.held_reels||{}).map(Number))
        setReSpinsLeft(d.re_spins_left||0)
        const tw=d.total_win||0
        if(tw>0){ setTotalWin(p=>p+tw); Sounds.hop() }
        if(d.expanding_wilds?.length>0){
          showToast('🌟 NEW WILD!',1500); Sounds.go()
        }
        setPhase('respinning')
      },3200)
    })

    // Round fully complete (all re-spins done)
    s.on('sb:round_complete',(d:any)=>{
      clearTimeout(safetyRef.current)
      // Clear all reel timers so no pending setPhase('respinning') can fire after this
      timers.current.forEach(clearTimeout); timers.current=[]
      setSpinning(Array(NUM_REELS).fill(false))
      const tw=d.total_win||0
      if(typeof d.balance==='number') setBalance(d.balance)
      setRoundWin(tw)
      setWildReels([])
      setReSpinsLeft(0)
      if(tw>=50){
        setShowBigWin(true)
        Sounds.cashout()
      } else if(tw>0){
        showToast(`🏆 WIN ₹${tw.toFixed(2)}!`,2500)
        Sounds.cashout()
      }
      setPhase('idle')
    })

    s.on('sb:error',(d:any)=>{ showToast(`❌ ${d.error}`); setPhase('idle') })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return ()=>{ s.disconnect() }
  },[token,triggerSpin])

  // Safety net: reset phase if stuck for 6s
  const armSafety=()=>{
    clearTimeout(safetyRef.current)
    safetyRef.current=setTimeout(()=>{
      setPhase('idle'); setSpinning(Array(NUM_REELS).fill(false))
    },6000)
  }

  // Spin
  const handleSpin=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='idle') return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    setPhase('spinning'); setWins([]); setTotalWin(0); setRoundWin(0)
    Sounds.go()
    armSafety()
    s.emit('sb:spin',{player_id:playerId,operator_id:operatorId,stake})
  }

  const isSpinning=spinning.some(s=>s)||phase==='spinning'
  const canSpin=!isSpinning&&phase==='idle'
  const STAKES=[5,10,20,50,100,200,500]

  return(
    <div style={{
      display:'flex',flexDirection:'column',height:'100%',
      background:'radial-gradient(ellipse at top,#0d0050 0%,#050018 50%,#000008 100%)',
      color:'#fff',fontFamily:'system-ui,sans-serif',userSelect:'none',overflow:'hidden',
      position:'relative',
    }}>
      <style>{`
        @keyframes sbToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes sbPulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes sbFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes sbNeon{0%,100%{text-shadow:0 0 8px #ff9900,0 0 16px #ff6600}50%{text-shadow:0 0 20px #ff9900,0 0 40px #ff6600,0 0 60px #ff3300}}
        .sb-spin:hover{transform:scale(1.05)}.sb-spin:active{transform:scale(.96)}
      `}</style>

      {/* Stars background */}
      <div style={{position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none'}}>
        {Array.from({length:40},(_,i)=>(
          <div key={i} style={{
            position:'absolute',
            left:`${Math.random()*100}%`,top:`${Math.random()*100}%`,
            width:Math.random()*2+1,height:Math.random()*2+1,
            borderRadius:'50%',background:'white',
            opacity:Math.random()*0.7+0.2,
            animation:`sbPulse ${1.5+Math.random()*2}s ${Math.random()*2}s infinite`,
          }}/>
        ))}
      </div>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.6)',
        borderBottom:'1px solid rgba(255,150,0,.2)',flexShrink:0,zIndex:10}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16,animation:'sbFloat 2s infinite'}}>⭐</span>
          <strong style={{fontSize:12,letterSpacing:1,
            animation:'sbNeon 2s infinite',color:'#ff9900'}}>STARBURST</strong>
          {reSpinsLeft>0&&(
            <span style={{background:'rgba(255,100,0,.3)',border:'1px solid rgba(255,100,0,.6)',
              borderRadius:10,padding:'1px 7px',fontSize:10,fontWeight:700,color:'#ff9900'}}>
              RE-SPIN ×{reSpinsLeft}
            </span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(255,150,0,.1)',border:'1px solid rgba(255,150,0,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#ffd700',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:50,left:'50%',zIndex:60,
          animation:'sbToast 2.2s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#ffd700',padding:'7px 18px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(255,150,0,.4)'}}>
          {toast}
        </div>
      )}

      {/* Big Win overlay */}
      {showBigWin&&(
        <BigWin amount={roundWin} onDone={()=>{ setShowBigWin(false) }}/>
      )}

      {/* ── Reel area ─────────────────────────────────────────────────────── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center',padding:'8px',zIndex:5}}>

        {/* Cabinet */}
        <div style={{
          background:'rgba(0,0,20,.7)',
          border:'2px solid rgba(255,150,0,.3)',borderRadius:14,
          padding:'10px 8px 8px',width:'100%',maxWidth:600,
          boxShadow:'0 0 40px rgba(255,100,0,.1),inset 0 1px 0 rgba(255,255,255,.04)',
          position:'relative',
        }}>
          {/* Win display */}
          <div style={{textAlign:'center',marginBottom:8,height:26}}>
            {totalWin>0?(
              <span style={{fontSize:20,fontWeight:900,color:'#ffd700',
                textShadow:'0 0 12px #ff9900'}}>
                ₹{totalWin.toFixed(2)}
              </span>
            ):(
              <span style={{fontSize:11,color:'rgba(255,255,255,.18)'}}>
                {isSpinning?'✨ Spinning...':phase==='expanding'?'🌟 Wild Expanding...':'Win Both Ways'}
              </span>
            )}
          </div>

          {/* Direction indicators */}
          <div style={{display:'flex',justifyContent:'space-between',
            marginBottom:4,padding:'0 4px'}}>
            <span style={{fontSize:9,color:'rgba(255,150,0,.5)',fontWeight:700}}>← RTL</span>
            <span style={{fontSize:9,color:'rgba(255,150,0,.5)',fontWeight:700}}>LTR →</span>
          </div>

          {/* Reels */}
          <div ref={wrapRef} style={{display:'flex',gap:GAP,width:'100%',justifyContent:'center'}}>
            {Array(NUM_REELS).fill(null).map((_,r)=>(
              <Reel key={r}
                strip={strips[r]} spinning={spinning[r]} targetY={targetY}
                winRows={reelWinRows(r)} isWild={wildReels.includes(r)}
                cellW={CELL_W} cellH={CELL_H} reelIdx={r}
              />
            ))}
          </div>

          {/* Win lines */}
          {wins.length>0&&(
            <div style={{marginTop:6,display:'flex',flexWrap:'wrap',
              justifyContent:'center',gap:3}}>
              {wins.map((w,i)=>(
                <div key={i} style={{
                  background:`${SYM_GLOW[w.symbol]||'#fff'}22`,
                  border:`1px solid ${SYM_GLOW[w.symbol]||'#fff'}66`,
                  borderRadius:10,padding:'1px 6px',fontSize:9,fontWeight:700,
                  color:SYM_GLOW[w.symbol]||'#fff',
                }}>
                  {w.direction==='rtl'?'←':'→'} L{w.line} ₹{w.payout.toFixed(1)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Controls ────────────────────────────────────────────────────────── */}
      <div style={{
        background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.7))',
        borderTop:'1px solid rgba(255,150,0,.1)',
        padding:'7px 10px 10px',flexShrink:0,zIndex:10,
      }}>
        {/* Stake */}
        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>BET:</span>
          <div style={{display:'flex',gap:3,overflowX:'auto',flex:1,paddingBottom:1}}>
            {STAKES.map(sv=>(
              <button key={sv} onClick={()=>!isSpinning&&setStake(sv)}
                style={{
                  borderRadius:5,padding:'3px 7px',cursor:isSpinning?'default':'pointer',
                  border:stake===sv?'1.5px solid #ff9900':'1.5px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(255,150,0,.18)':'rgba(255,255,255,.04)',
                  color:stake===sv?'#ff9900':'rgba(255,255,255,.4)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',flexShrink:0,transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
        </div>

        {/* Spin row */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 68px 1fr',gap:8,alignItems:'center'}}>
          <div style={{fontSize:9,color:'rgba(255,255,255,.3)',lineHeight:1.6}}>
            10 lines<br/>
            <span style={{color:'rgba(255,150,0,.5)',fontWeight:700}}>₹{stake} bet</span>
          </div>

          <button className="sb-spin" onClick={handleSpin} disabled={!canSpin}
            style={{
              borderRadius:50,width:68,height:68,border:'none',
              cursor:canSpin?'pointer':'default',fontWeight:900,fontSize:10,
              background:canSpin
                ?'linear-gradient(135deg,#ff9900,#ff4400)'
                :'rgba(255,255,255,.07)',
              color:canSpin?'#fff':'rgba(255,255,255,.2)',
              transition:'all .15s',
              boxShadow:canSpin?'0 0 20px rgba(255,100,0,.5)':'none',
            }}>
            {isSpinning?'⏳':phase==='expanding'||phase==='respinning'?'🌟':'SPIN'}
          </button>

          <div style={{textAlign:'right',fontSize:9,color:'rgba(255,255,255,.3)',lineHeight:1.6}}>
            {roundWin>0&&!showBigWin?(
              <span style={{color:'#ffd700',fontWeight:700}}>
                Round win<br/>₹{roundWin.toFixed(2)}
              </span>
            ):(
              <span>~96% RTP<br/>Win Both Ways</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
